import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { API_BASE_URL } from '../config';
import { Package, Truck, CreditCard, CheckCircle, AlertCircle } from 'lucide-react';
import './UserProfilePage.css';

export default function UserProfilePage() {
  const navigate = useNavigate();
  const [userData, setUserData] = useState(null);
  const [orders, setOrders] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isEditing, setIsEditing] = useState(false);
  const [editedData, setEditedData] = useState({});
  const [error, setError] = useState(null);
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [deletePassword, setDeletePassword] = useState('');
  const [changePasswordData, setChangePasswordData] = useState({
    currentPassword: '',
    newPassword: '',
    confirmNewPassword: ''
  });
  const [passwordErrors, setPasswordErrors] = useState([]);
  const [changePasswordMode, setChangePasswordMode] = useState(false);
  const [successMessage, setSuccessMessage] = useState('');
  const [activeTab, setActiveTab] = useState('profile');

  useEffect(() => {
    const token = localStorage.getItem('token');
    if (!token) {
      navigate('/login');
      return;
    }

    const fetchUserData = async () => {
      try {
        setIsLoading(true);
        const [userResponse, ordersResponse] = await Promise.all([
          fetch(`${API_BASE_URL}/api/users/profile`, {
            headers: {
              'Authorization': `Bearer ${token}`,
              'Content-Type': 'application/json'
            }
          }),
          fetch(`${API_BASE_URL}/api/orders/my-orders`, {
            headers: {
              'Authorization': `Bearer ${token}`,
              'Content-Type': 'application/json'
            }
          })
        ]);

        if (!userResponse.ok || !ordersResponse.ok) {
          if (userResponse.status === 401 || ordersResponse.status === 401) {
            localStorage.removeItem('token');
            navigate('/login');
            return;
          }
          throw new Error('Nie udało się pobrać danych');
        }

        const [userData, ordersData] = await Promise.all([
          userResponse.json(),
          ordersResponse.json()
        ]);

        setUserData(userData);
        setOrders(ordersData);
        setEditedData({
          name: userData.name || '',
          surname: userData.surname || '',
          phoneNumber: userData.phoneNumber || '',
          address: {
            street: userData.address?.street || '',
            city: userData.address?.city || '',
            postalCode: userData.address?.postalCode || '',
            country: userData.address?.country || 'Poland'
          },
          newsletter: userData.newsletter || false
        });
      } catch (err) {
        setError(err.message);
        console.error('Error fetching data:', err);
      } finally {
        setIsLoading(false);
      }
    };

    fetchUserData();
    
    // Set up polling for order updates
    const intervalId = setInterval(async () => {
      try {
        const response = await fetch(`${API_BASE_URL}/api/orders/my-orders`, {
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          }
        });
        
        if (response.ok) {
          const ordersData = await response.json();
          setOrders(ordersData);
        }
      } catch (error) {
        console.error('Error polling orders:', error);
      }
    }, 30000); // Poll every 30 seconds

    return () => clearInterval(intervalId);
  }, [navigate]);

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    
    if (name.includes('.')) {
      // Obsługa pól zagnieżdżonych (np. address.street)
      const [parent, child] = name.split('.');
      setEditedData(prev => ({
        ...prev,
        [parent]: {
          ...prev[parent],
          [child]: value
        }
      }));
    } else if (type === 'checkbox') {
      setEditedData(prev => ({
        ...prev,
        [name]: checked
      }));
    } else {
      setEditedData(prev => ({
        ...prev,
        [name]: value
      }));
    }
  };

  const handlePasswordChange = (e) => {
    const { name, value } = e.target;
    setChangePasswordData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const validatePassword = (password) => {
    const errors = [];
    if (password.length < 8) {
      errors.push('Hasło musi mieć co najmniej 8 znaków');
    }
    if (!/[A-Z]/.test(password)) {
      errors.push('Hasło musi zawierać co najmniej jedną wielką literę');
    }
    if (!/[a-z]/.test(password)) {
      errors.push('Hasło musi zawierać co najmniej jedną małą literę');
    }
    if (!/[0-9]/.test(password)) {
      errors.push('Hasło musi zawierać co najmniej jedną cyfrę');
    }
    if (!/[!@#$%^&*(),.?":{}|<>]/.test(password)) {
      errors.push('Hasło musi zawierać co najmniej jeden znak specjalny');
    }
    return errors;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const token = localStorage.getItem('token');
    
    try {
      setIsLoading(true);
      const response = await fetch(`${API_BASE_URL}/api/users/profile`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(editedData)
      });

      if (!response.ok) {
        throw new Error('Nie udało się zaktualizować profilu');
      }

      const updatedData = await response.json();
      setUserData(updatedData);
      setIsEditing(false);
      setSuccessMessage('Profil został zaktualizowany pomyślnie');
      
      // Usuń komunikat sukcesu po 3 sekundach
      setTimeout(() => {
        setSuccessMessage('');
      }, 3000);
    } catch (err) {
      setError(err.message);
      console.error('Error updating profile:', err);
    } finally {
      setIsLoading(false);
    }
  };

  const handleChangePassword = async (e) => {
    e.preventDefault();
    const { currentPassword, newPassword, confirmNewPassword } = changePasswordData;
    
    // Walidacja
    if (newPassword !== confirmNewPassword) {
      setPasswordErrors(['Hasła nie są zgodne']);
      return;
    }

    const passwordValidationErrors = validatePassword(newPassword);
    if (passwordValidationErrors.length > 0) {
      setPasswordErrors(passwordValidationErrors);
      return;
    }

    const token = localStorage.getItem('token');
    
    try {
      setIsLoading(true);
      const response = await fetch(`${API_BASE_URL}/api/users/change-password`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          currentPassword,
          newPassword
        })
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || 'Nie udało się zmienić hasła');
      }

      setChangePasswordMode(false);
      setChangePasswordData({
        currentPassword: '',
        newPassword: '',
        confirmNewPassword: ''
      });
      setPasswordErrors([]);
      setSuccessMessage('Hasło zostało zmienione pomyślnie');
      
      // Usuń komunikat sukcesu po 3 sekundach
      setTimeout(() => {
        setSuccessMessage('');
      }, 3000);
    } catch (err) {
      setError(err.message);
      console.error('Error changing password:', err);
    } finally {
      setIsLoading(false);
    }
  };

  const handleDeleteAccount = async () => {
    const token = localStorage.getItem('token');
    
    try {
      setIsLoading(true);
      const response = await fetch(`${API_BASE_URL}/api/users/delete-account`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          password: deletePassword
        })
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || 'Nie udało się usunąć konta');
      }

      localStorage.removeItem('token');
      localStorage.removeItem('userId');
      alert('Twoje konto zostało usunięte.');
      navigate('/');
    } catch (err) {
      setError(err.message);
      console.error('Error deleting account:', err);
    } finally {
      setIsLoading(false);
      setDeleteModalOpen(false);
      setDeletePassword('');
    }
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'new':
        return <CreditCard className="status-icon new" />;
      case 'processing':
        return <Package className="status-icon processing" />;
      case 'shipped':
        return <Truck className="status-icon shipped" />;
      case 'delivered':
        return <CheckCircle className="status-icon delivered" />;
      case 'cancelled':
        return <AlertCircle className="status-icon cancelled" />;
      default:
        return <Package className="status-icon" />;
    }
  };

  const getStatusText = (status) => {
    switch (status) {
      case 'new':
        return 'Oczekuje na płatność';
      case 'processing':
        return 'W trakcie realizacji';
      case 'shipped':
        return 'Wysłane';
      case 'delivered':
        return 'Dostarczone';
      case 'cancelled':
        return 'Anulowane';
      default:
        return 'Status nieznany';
    }
  };

  if (isLoading) {
    return <div className="loading">Ładowanie...</div>;
  }

  if (error) {
    return <div className="error">{error}</div>;
  }

  return (
    <div className="user-profile-container">
      <div className="profile-tabs">
        <button
          className={`tab-button ${activeTab === 'profile' ? 'active' : ''}`}
          onClick={() => setActiveTab('profile')}
        >
          Profil
        </button>
        <button
          className={`tab-button ${activeTab === 'orders' ? 'active' : ''}`}
          onClick={() => setActiveTab('orders')}
        >
          Zamówienia {orders.length > 0 && <span className="order-count">{orders.length}</span>}
        </button>
      </div>

      {activeTab === 'profile' ? (
        <>
          <h1>Mój profil</h1>
          
          {successMessage && (
            <div className="success-message">
              {successMessage}
            </div>
          )}
          
          {error && (
            <div className="error-message">
              <p>{error}</p>
              <button onClick={() => setError(null)}>Zamknij</button>
            </div>
          )}
          
          {changePasswordMode ? (
            <div className="profile-section">
              <h2>Zmiana hasła</h2>
              <form onSubmit={handleChangePassword}>
                <div className="form-group">
                  <label htmlFor="currentPassword">Obecne hasło</label>
                  <input
                    type="password"
                    id="currentPassword"
                    name="currentPassword"
                    value={changePasswordData.currentPassword}
                    onChange={handlePasswordChange}
                    required
                  />
                </div>
                
                <div className="form-group">
                  <label htmlFor="newPassword">Nowe hasło</label>
                  <input
                    type="password"
                    id="newPassword"
                    name="newPassword"
                    value={changePasswordData.newPassword}
                    onChange={handlePasswordChange}
                    required
                  />
                </div>
                
                <div className="form-group">
                  <label htmlFor="confirmNewPassword">Potwierdź nowe hasło</label>
                  <input
                    type="password"
                    id="confirmNewPassword"
                    name="confirmNewPassword"
                    value={changePasswordData.confirmNewPassword}
                    onChange={handlePasswordChange}
                    required
                  />
                </div>
                
                {passwordErrors.length > 0 && (
                  <ul className="password-errors">
                    {passwordErrors.map((error, index) => (
                      <li key={index}>{error}</li>
                    ))}
                  </ul>
                )}
                
                <div className="form-buttons">
                  <button type="button" onClick={() => {
                    setChangePasswordMode(false);
                    setPasswordErrors([]);
                    setChangePasswordData({
                      currentPassword: '',
                      newPassword: '',
                      confirmNewPassword: ''
                    });
                  }}>
                    Anuluj
                  </button>
                  <button type="submit" disabled={isLoading}>
                    {isLoading ? 'Zapisywanie...' : 'Zmień hasło'}
                  </button>
                </div>
              </form>
            </div>
          ) : isEditing ? (
            <div className="profile-section">
              <h2>Edycja profilu</h2>
              <form onSubmit={handleSubmit}>
                <div className="form-row">
                  <div className="form-group">
                    <label htmlFor="name">Imię</label>
                    <input
                      type="text"
                      id="name"
                      name="name"
                      value={editedData.name}
                      onChange={handleInputChange}
                      required
                    />
                  </div>
                  
                  <div className="form-group">
                    <label htmlFor="surname">Nazwisko</label>
                    <input
                      type="text"
                      id="surname"
                      name="surname"
                      value={editedData.surname}
                      onChange={handleInputChange}
                      required
                    />
                  </div>
                </div>
                
                <div className="form-group">
                  <label htmlFor="phoneNumber">Numer telefonu</label>
                  <input
                    type="tel"
                    id="phoneNumber"
                    name="phoneNumber"
                    value={editedData.phoneNumber}
                    onChange={handleInputChange}
                  />
                </div>
                
                <h3>Adres</h3>
                
                <div className="form-group">
                  <label htmlFor="street">Ulica i numer</label>
                  <input
                    type="text"
                    id="street"
                    name="address.street"
                    value={editedData.address.street}
                    onChange={handleInputChange}
                  />
                </div>
                
                <div className="form-row">
                  <div className="form-group">
                    <label htmlFor="city">Miasto</label>
                    <input
                      type="text"
                      id="city"
                      name="address.city"
                      value={editedData.address.city}
                      onChange={handleInputChange}
                    />
                  </div>
                  
                  <div className="form-group">
                    <label htmlFor="postalCode">Kod pocztowy</label>
                    <input
                      type="text"
                      id="postalCode"
                      name="address.postalCode"
                      value={editedData.address.postalCode}
                      onChange={handleInputChange}
                    />
                  </div>
                </div>
                
                <div className="form-group checkbox-group">
                  <input
                    type="checkbox"
                    id="newsletter"
                    name="newsletter"
                    checked={editedData.newsletter}
                    onChange={handleInputChange}
                  />
                  <label htmlFor="newsletter">Chcę otrzymywać powiadomienia o nowościach i promocjach</label>
                </div>
                
                <div className="form-buttons">
                  <button type="button" onClick={() => setIsEditing(false)}>
                    Anuluj
                  </button>
                  <button type="submit" disabled={isLoading}>
                    {isLoading ? 'Zapisywanie...' : 'Zapisz zmiany'}
                  </button>
                </div>
              </form>
            </div>
          ) : (
            <>
              <div className="profile-section">
                <h2>Dane osobowe</h2>
                <div className="profile-details">
                  <div className="detail-row">
                    <strong>Email:</strong>
                    <span>{userData.email}</span>
                  </div>
                  <div className="detail-row">
                    <strong>Imię i nazwisko:</strong>
                    <span>{userData.name} {userData.surname}</span>
                  </div>
                  {userData.phoneNumber && (
                    <div className="detail-row">
                      <strong>Numer telefonu:</strong>
                      <span>{userData.phoneNumber}</span>
                    </div>
                  )}
                  <div className="detail-row">
                    <strong>Newsletter:</strong>
                    <span>{userData.newsletter ? 'Tak' : 'Nie'}</span>
                  </div>
                </div>
              </div>
              
              {userData.address && (userData.address.street || userData.address.city || userData.address.postalCode) && (
                <div className="profile-section">
                  <h2>Adres dostawy</h2>
                  <div className="profile-details">
                    {userData.address.street && (
                      <div className="detail-row">
                        <strong>Ulica i numer:</strong>
                        <span>{userData.address.street}</span>
                      </div>
                    )}
                    {userData.address.city && (
                      <div className="detail-row">
                        <strong>Miasto:</strong>
                        <span>{userData.address.city}</span>
                      </div>
                    )}
                    {userData.address.postalCode && (
                      <div className="detail-row">
                        <strong>Kod pocztowy:</strong>
                        <span>{userData.address.postalCode}</span>
                      </div>
                    )}
                    <div className="detail-row">
                      <strong>Kraj:</strong>
                      <span>{userData.address.country || 'Polska'}</span>
                    </div>
                  </div>
                </div>
              )}
              
              <div className="profile-actions">
                <button className="btn-edit" onClick={() => setIsEditing(true)}>
                  Edytuj profil
                </button>
                <button className="btn-password" onClick={() => setChangePasswordMode(true)}>
                  Zmień hasło
                </button>
                <button className="btn-delete" onClick={() => setDeleteModalOpen(true)}>
                  Usuń konto
                </button>
              </div>
            </>
          )}
        </>
      ) : (
        <div className="orders-section">
          <h2>Moje zamówienia</h2>
          {orders.length === 0 ? (
            <div className="no-orders">
              <p>Nie masz jeszcze żadnych zamówień</p>
            </div>
          ) : (
            <div className="orders-list">
              {orders.map(order => (
                <div key={order._id} className="order-card">
                  <div className="order-header">
                    <h3>Zamówienie #{order.orderNumber}</h3>
                    <span className={`order-status ${order.status}`}>
                      {getStatusIcon(order.status)}
                      {getStatusText(order.status)}
                    </span>
                  </div>
                  <div className="order-details">
                    <div className="order-info">
                      <p><strong>Data zamówienia:</strong> {new Date(order.createdAt).toLocaleString('pl-PL')}</p>
                      <p><strong>Wartość:</strong> {order.totalAmount.toFixed(2)} zł</p>
                      {order.trackingNumber && (
                        <p><strong>Numer przesyłki:</strong> {order.trackingNumber}</p>
                      )}
                    </div>
                    <div className="order-items">
                      <h4>Produkty:</h4>
                      <ul>
                        {order.items.map(item => (
                          <li key={item._id}>
                            {item.productName} x {item.quantity} - {(item.price * item.quantity).toFixed(2)} zł
                          </li>
                        ))}
                      </ul>
                    </div>
                    {order.status === 'shipped' && order.trackingNumber && (
                      <a
                        href={`https://inpost.pl/sledzenie-przesylek?number=${order.trackingNumber}`}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="track-button"
                      >
                        Śledź przesyłkę
                      </a>
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}
      
      {deleteModalOpen && (
        <div className="delete-modal-overlay">
          <div className="delete-modal">
            <h2>Usuwanie konta</h2>
            <p>Czy na pewno chcesz usunąć swoje konto? Ta operacja jest nieodwracalna.</p>
            <p>Wprowadź swoje hasło, aby potwierdzić:</p>
            <input
              type="password"
              value={deletePassword}
              onChange={(e) => setDeletePassword(e.target.value)}
              placeholder="Twoje hasło"
            />
            <div className="modal-buttons">
              <button onClick={() => {
                setDeleteModalOpen(false);
                setDeletePassword('');
              }}>
                Anuluj
              </button>
              <button 
                onClick={handleDeleteAccount}
                disabled={!deletePassword || isLoading}
                className="btn-delete"
              >
                {isLoading ? 'Usuwanie...' : 'Usuń konto'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
} 